import { useState } from 'react'
import { Copy, Check, X } from 'lucide-react'

// Bits shared between the card list (UserCard) and the HQ table (UsersTable)
// so the two views can never drift apart.

export const ROLE_LABELS = { superadmin: 'Super Admin', admin: 'Admin', manager: 'Manager', staff: 'Staff' }
export const ROLE_COLORS = {
  superadmin: 'bg-purple-100 text-purple-700',
  admin: 'bg-blue-100 text-blue-700',
  manager: 'bg-green-100 text-green-700',
  staff: 'bg-gray-100 text-gray-700',
}

export function getInitials(name) {
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
}

export function CopyablePassword({ password, onDismiss }) {
  const [copied, setCopied] = useState(false)
  function copy() {
    navigator.clipboard?.writeText(password)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <div className="mt-2 flex items-center space-x-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
      <div className="flex-1 min-w-0">
        <p className="text-xs text-amber-600">New temp password</p>
        <p className="font-mono font-semibold text-amber-800 tracking-wider">{password}</p>
      </div>
      <button onClick={copy} className="text-amber-600">
        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
      </button>
      <button onClick={onDismiss} className="text-amber-400">
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}

// Type-DELETE confirmation box. Owns its input/busy/error state; calls
// onDelete(user.id) and closes itself via onClose when the delete succeeds.
export function DeleteConfirm({ user, onDelete, onClose }) {
  const [input, setInput] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState('')

  async function confirm() {
    if (input !== 'DELETE' || deleting) return
    setDeleting(true)
    setError('')
    const result = await onDelete(user.id)
    setDeleting(false)
    if (result && !result.success) {
      setError(result.message || 'Failed to delete account. Please try again.')
      return
    }
    onClose()
  }

  return (
    <div className="mt-2 bg-red-50 border border-red-200 rounded-xl px-3 py-3 space-y-2">
      <p className="text-sm font-semibold text-red-800">Delete {user.name}?</p>
      <p className="text-xs text-red-600">This permanently removes their account and all associated data. This cannot be undone.</p>
      <p className="text-xs font-semibold text-red-700">Type <span className="font-mono bg-red-100 px-1 rounded">DELETE</span> to confirm:</p>
      <input
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder="Type DELETE"
        className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-red-400 outline-none bg-white"
        autoFocus
      />
      {error && (
        <p className="text-xs font-medium text-red-700 bg-red-100 border border-red-200 rounded-lg px-2.5 py-2">{error}</p>
      )}
      <div className="flex space-x-2 pt-1">
        <button
          onClick={onClose}
          disabled={deleting}
          className="flex-1 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm font-semibold disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={confirm}
          disabled={input !== 'DELETE' || deleting}
          className="flex-1 py-2 rounded-lg bg-red-600 disabled:opacity-40 text-white text-sm font-semibold"
        >
          {deleting ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </div>
  )
}
